import { useObserver, useSubtree } from "subtree.js/react";
import {
  breaksMuchLater,
  formatBreakTime,
  OTHER_KINDS,
  type OtherKind,
  segmentKindLabel,
} from "../../core/primitives/lineup";
import { formatCents } from "../../core/primitives/live";
import { LineupPickerActions, LineupPickerState } from "./lineup-picker-model";

/**
 * What a stream will run: boxes from the shop's queue, and anything else — an
 * unboxing, a guest, an autograph session. At Go live it opens the stream, with
 * a way to skip and add as it goes; in the cockpit it adds to the line-up.
 */
export function LineupPicker() {
  const state = useSubtree(LineupPickerState);
  const actions = useSubtree(LineupPickerActions);
  const observer = useObserver();
  const open = observer.watch(state.open);
  const mode = observer.watch(state.mode);
  const boxes = observer.watch(state.boxes);
  const boxesStatus = observer.watch(state.boxesStatus);
  const picked = observer.watch(state.picked);
  const extras = observer.watch(state.extras);
  const draft = observer.watch(state.extraDraft);
  const locations = observer.watch(state.locations);
  const locationId = observer.watch(state.locationId);
  const submit = observer.watch(state.submit);

  if (!open) return null;
  const goLive = mode === "go-live";
  const now = new Date();
  const count = picked.length + extras.length;

  return (
    <div className="scrim items-center justify-center p-4">
      <div className="modal flex max-h-[90dvh] w-full max-w-lg flex-col gap-4 overflow-y-auto p-6">
        <div>
          <h2 className="text-base font-semibold">{goLive ? "What's on this stream?" : "Add to the line-up"}</h2>
          <p className="text-sm opacity-60">
            {goLive
              ? "Pick the boxes you will break. Buyers who hold spots in them are told when you go live."
              : "Buyers who hold spots in a box you add are told right away."}
          </p>
        </div>

        {goLive && locations.length > 1 && (
          <label className="block">
            <span className="label">Streaming from</span>
            <select
              className="field"
              value={locationId ?? ""}
              onChange={(event) => actions.selectLocation(event.target.value)}
            >
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </label>
        )}

        <section className="space-y-2">
          <h3 className="eyebrow">Boxes on sale</h3>
          {boxesStatus.kind === "loading" && boxes.length === 0 ? (
            <p className="text-sm opacity-60">Loading your boxes…</p>
          ) : boxesStatus.kind === "error" ? (
            <p className="note note-danger">{boxesStatus.message}</p>
          ) : boxes.length === 0 ? (
            <p className="note">No box is waiting. Put one on sale from the Live page.</p>
          ) : (
            boxes.map((box) => {
              const order = picked.indexOf(box.id);
              const later = breaksMuchLater(box, now);
              return (
                <label key={box.id} className="panel flex cursor-pointer items-start gap-3 p-3">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={order >= 0}
                    onChange={() => actions.toggleBox(box.id)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate font-medium">{box.title}</span>
                      {order >= 0 && <span className="badge badge-accent">#{order + 1}</span>}
                    </span>
                    <span className="block text-sm opacity-60">
                      {box.soldCount}/{box.slotCount} spots · {formatCents(box.grossSoldCents)} taken
                      {box.breakNotBefore ? ` · breaks ${formatBreakTime(box.breakNotBefore, now)}` : ""}
                    </span>
                    {later && box.breakNotBefore && (
                      <span className="mt-1 block text-xs text-[color:var(--color-warn,inherit)]">
                        Scheduled for {formatBreakTime(box.breakNotBefore, now)}, well after now. Buyers planned for
                        that time.
                      </span>
                    )}
                  </span>
                </label>
              );
            })
          )}
        </section>

        <section className="space-y-2">
          <h3 className="eyebrow">Something else</h3>
          <div className="flex gap-2">
            <select
              className="field w-auto"
              value={draft.kind}
              onChange={(event) => actions.setExtraKind(event.target.value as OtherKind)}
              aria-label="Kind"
            >
              {OTHER_KINDS.map((k) => (
                <option key={k.kind} value={k.kind}>
                  {k.label}
                </option>
              ))}
            </select>
            <input
              className="field min-w-0 flex-1"
              placeholder="New arrivals, a guest at the counter…"
              value={draft.title}
              onChange={(event) => actions.setExtraTitle(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") actions.addExtra();
              }}
            />
            <button type="button" className="btn-secondary" disabled={!draft.title.trim()} onClick={actions.addExtra}>
              Add
            </button>
          </div>
          {extras.map((extra, index) => (
            <div key={`${extra.kind}:${extra.title}`} className="flex items-center gap-2 text-sm">
              <span className="badge badge-neutral">{segmentKindLabel(extra.kind)}</span>
              <span className="min-w-0 flex-1 truncate">{extra.title}</span>
              <button type="button" className="btn-ghost btn-xs" onClick={() => actions.removeExtra(index)}>
                Remove
              </button>
            </div>
          ))}
        </section>

        {submit.error && <p className="note note-danger">{submit.error}</p>}

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn-primary"
            disabled={submit.busy || (!goLive && count === 0)}
            onClick={() => void actions.confirm()}
          >
            {submit.busy ? "Working…" : goLive ? (count > 0 ? `Go live with ${count}` : "Go live") : `Add ${count}`}
          </button>
          {goLive && count > 0 && (
            <button type="button" className="btn-ghost" disabled={submit.busy} onClick={() => void actions.skip()}>
              Skip, I'll add during the stream
            </button>
          )}
          <button type="button" className="btn-ghost ml-auto" disabled={submit.busy} onClick={actions.close}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
